import axios, { AxiosRequestConfig } from 'axios';
import { API_AUTH_V1_REFRESH } from 'consts';
import { AxiosMethod } from 'enums';

export const BASE_URL = process.env.REACT_APP_BACKEND_ENDPOINT;

const axiosInstance = axios.create({
  baseURL: BASE_URL,
});

let isRefreshing = false;
let failedQueue: {
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const authInterceptor = (config: AxiosRequestConfig) => {
  const newConfig = { ...config };
  try {
    const { accessToken } = JSON.parse(localStorage.UserStore);
    if (accessToken) {
      if (!newConfig.headers) {
        newConfig.headers = {};
      }
      newConfig.headers.Authorization = `Bearer ${accessToken}`;
    }
  } catch (e) {
    // console.error('Error parsing UserStore from localStorage', e);
  }
  return newConfig;
};

axiosInstance.interceptors.request.use(authInterceptor);

axiosInstance.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalRequest = err.config;

    if (err.response.status === 401 && !originalRequest.isRetry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((catchError) => Promise.reject(catchError));
      }

      originalRequest.isRetry = true;
      isRefreshing = true;

      try {
        const storage = JSON.parse(localStorage.UserStore);
        const { refreshToken } = storage;
        const {
          data: { accessToken: newAccessToken, refreshToken: newRefreshToken },
        } = await axios({
          method: AxiosMethod.POST,
          url: API_AUTH_V1_REFRESH,
          data: { refreshToken },
        });

        storage.accessToken = newAccessToken;
        storage.refreshToken = newRefreshToken;
        localStorage.setItem('UserStore', JSON.stringify(storage));
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);
        return await axiosInstance(originalRequest);
      } catch (e) {
        processQueue(e, null);
        localStorage.removeItem('UserStore');
        window.location.href = '/login';
        return await Promise.reject(e);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(err);
  },
);

export { axiosInstance };
